<template lang="pug">
  table.calendar-table
    thead
      tr
        th(@click="prevMonth").prev.available.v-drp__css-icon-wrapper
          i.arrow-left.v-drp__css-icon
        th(colspan="5").month
          | {{ monthName }}
          // select year
          select(v-if="showYearSelect" v-model="year").yearselect
            option(v-for="(item, index) in years" :value="item" :key="index") {{ item }}
          span(v-else style="margin-left: 5px") {{ year }}
        th(@click="nextMonth").next.available.v-drp__css-icon-wrapper
          i.arrow-right.v-drp__css-icon
    tbody
      tr
        th(v-for="(weekDay, dayIndex) in daysOfWeek" :key="dayIndex") 
          | {{ weekDay }}
      tr(v-for="(dateRow, rowIndex) in calendar" :key="rowIndex")
        td(
          v-for="(date, dateIndex) in dateRow"
          :key="dateIndex"
          :class="dayClass(date)"
          @click="selectDate(date)"
          @mouseover="hoverDate(date)"
        ).calendar-cell
          div.calendar-cell__content {{ date | dateNum }}
</template>

<script>
import datefns from 'date-fns';

export default {
  props: {
    // calendar's reference day
    day: {
      type: Number, // date
      default: Date.now()
    },
    // date range if object
    value: {
      type: [ Number, Object ],
      default: Date.now()
    },
    // hover range
    hover: {
      type: Object
    },
    showYearSelect: {
      type: Boolean,
      default: false
    },
    minYear: {
      type: Number,
      default: datefns.getYear(datefns.subYears(Date.now(), 100))
    },
    maxYear: {
      type: Number,
      default: datefns.getYear(datefns.addYears(Date.now(), 100))
    }
  },
  data: () => ({
    firstDayOfWeek: 0,
    daysOfWeek: [
      'Su',
      'Mo',
      'Tu',
      'We',
      'Th',
      'Fr',
      'Sa'
    ]
  }),
  filters: {
    dateNum (value) {
      return datefns.getDate(value);
    }
  },
  computed: {
    month () {
      return datefns.getMonth(this.day);
    },
    monthName () {
      return datefns.format(this.day, 'MMMM');
    },
    year: {
      get () {
        return datefns.getYear(this.day);
      },
      set (year) {
        this.$emit('clickYear', datefns.setYear(this.day, year));
      }
    },
    years () {
      if (!this.showYearSelect) return [];
      let minYear = this.minYear;
      let maxYear = this.maxYear;
      const years = [];
      ++minYear;
      ++maxYear;
      for (let i = minYear; i <= maxYear; i++) years.push(i);
      return years;
    },
    calendar () {
      const minute = datefns.getMinutes(this.day);
      const second = datefns.getSeconds(this.day);
      const firstDay = datefns.startOfMonth(this.day);
      const lastMonth = datefns.getMonth(datefns.subMonths(this.day, 1));
      const lastYear = datefns.getYear(datefns.subMonths(this.day, 1));
      const dayOfWeek = datefns.getDate(firstDay);
      const daysInLastMonth = datefns.getDaysInMonth(datefns.subMonths(this.day, 1));

      // initialize a 6 rows x 7 columns array for the calendar
      const calendar = [];
      for (let i = 0; i < 6; i++) calendar[i] = [];

      // populate the calendar with date objects
      let startDay = daysInLastMonth - dayOfWeek + this.firstDayOfWeek + 1;
      if (startDay > daysInLastMonth) startDay -= 7;
      if (dayOfWeek === this.firstDayOfWeek) { startDay = daysInLastMonth - 6; }
      let curDate = new Date(lastYear, lastMonth, startDay, 12, minute, second).getTime();
      for (let i = 0, col = 0, row = 0; i < 42; i++, col++, curDate = datefns.addHours(curDate, 24).getTime()) {
        if (i > 0 && col % 7 === 0) { col = 0; row++; }
        calendar[row][col] = curDate;
      }
      return calendar;
    }
  },
  methods: {
    nextMonth () {
      this.$emit('clickNextMonth');
    },
    prevMonth () {
      this.$emit('clickPrevMonth');
    },
    selectDate (date) {
      this.$emit('input', date);
    },
    hoverDate (date) {
      if (!this.hover) return;
      const oldStart = typeof this.value === 'number' ? this.value : this.value.start;
      const hover = {};
      if (datefns.compareAsc(date, oldStart) > 0) {
        hover.start = oldStart;
        hover.end = date;
      } else {
        hover.end = oldStart;
        hover.start = date;
      }
      this.$emit('update:hover', hover);
    },
    dayClass (date) {
      const valueEnd = typeof this.value === 'number' ? this.value : this.value.end;
      const valueStart = typeof this.value === 'number' ? this.value : this.value.start;
      return {
        off: !datefns.isSameMonth(date, this.day),
        weekend: datefns.isWeekend(date),
        today: datefns.isToday(date),
        active: datefns.isSameDay(date, valueStart) || datefns.isSameDay(date, valueEnd),
        'in-range': this.hover &&
          ((datefns.compareAsc(date, valueStart) > -1 && datefns.compareDesc(date, valueEnd) > -1) ||
          (datefns.compareAsc(date, this.hover.start) > -1 && datefns.compareDesc(date, this.hover.end) > -1)),
        'start-date': datefns.isSameDay(date, valueStart),
        'end-date': datefns.isSameDay(date, valueEnd)
      };
    }
  }
};
</script>

<style lang="scss" scoped>
$cellActiveBgColor: #4285f4;
$cellInRangeBgColor: #eef4ff;
$cellSize: 34px;

table.calendar-table {
  width: 100%;
  margin: 0;
  border-spacing: 0;
  // ??? border-collapse
  border-collapse: collapse;

  // 移除其它 css 库的影响，比如 vuepress 的主题样式
  tr,
  th,
  td {
    border: none;
  }

  // TODO add renderCalendarCell function for API to custom the calendar cell style
  td,
  th {
    white-space: nowrap;
    text-align: center;
    vertical-align: middle;
    min-width: $cellSize;
    width: $cellSize;
    padding: $cellSize/2 0;
    height: 0;
    line-height: 0;
    font-size: 12px;
    // border-radius: 50%;
    white-space: nowrap;
    cursor: pointer;

    // border-radius: 4px;
  }

  td.calendar-cell {
    position: relative;

    &:hover {
      background-color: #eee;
      border-color: transparent;
      color: inherit;
    }
    &.today {
      font-weight: bold;
    }

    // items between start and end
    &.in-range {
      background-color: $cellInRangeBgColor;
      border-color: transparent;
      color: #000;
      border-radius: 0;
    }

    // 选中项 (active item)
    // TODO 开放 API，允许用户自定义 active 的样式，如何做 ？
    // TODO give an API for user to custom active class
    &.active,
    &.active:hover {
      border-color: transparent;
      color: #fff;

      &.start-date {
        border-top-left-radius: $cellSize;
        border-bottom-left-radius: $cellSize;

        // bootstraps style
        // border-radius: 4px 0 0 0;
      }

      &.end-date {
        // left-top, left-bottom
        border-top-right-radius: $cellSize;
        border-bottom-right-radius: $cellSize;

        // bootstraps style
        // border-radius: 0 0 4px 0;
      }

      .calendar-cell__content {
        position: absolute;
        top: 5%;
        left: 5%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        width: 90%;
        height: 90%;
        line-height: 1;
        border-radius: 999px;
        background-color: $cellActiveBgColor;

        // bootstraps style
        // top: 0%;
        // left: 0%;
        // width: 100%;
        // height: 100%;
        // border-radius: 0;
        // background-color: $cellActiveBgColor;

      }
    }

    // Items that do not belong to the current month（不在当前月份 Calendar 的 item）
    // see more: https://github.com/ly525/v-md-date-range-picker/wiki/styles#dayoff
    &.off,
    &.off.in-range,
    &.off.start-date,
    &.off.end-date {
      background-color: #fff;
      border-color: transparent;
      color: #999;

      .calendar-cell__content {
        background-color: #fff;
      }
    }
  }
}

// TODO need refactor by scss mixin
.v-drp__css-icon-wrapper {
  position: relative;

  .v-drp__css-icon {

    &.arrow-left:before {
      content: '';
      position: absolute;
      left: 50%;
      top: 8px;
      width: 7px;
      height: 7px;
      border-top: solid 2px currentColor;
      border-right: solid 2px currentColor;
      transform: rotate(-135deg);
    }

    &.arrow-right:before {
      content: '';
      position: absolute;
      right: 50%;
      top: 8px;
      width: 7px;
      height: 7px;
      border-top: solid 2px currentColor;
      border-right: solid 2px currentColor;
      transform: rotate(45deg);
    }
  }

}
</style>
